// Voice engines → Profiles (plan §6.5.2): every profile per engine with what
// uses it, and a typed form per engine built from the API's settings schema.
// Saving creates the next version; built-in defaults (today's env values)
// are read-only and get changed by duplicating them.
//
// Not here: ElevenLabs engine sync and "Listen" in the voice bench, which
// come with ElevenLabs provisioning (plan §7 B4).

import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../services/http";
import {
  createProfile,
  getProfileVersions,
  saveProfile,
  setProfileStatus,
  type EngineName,
  type Profile,
  type ProfilesResponse,
  type ProfileVersions,
} from "../../services/voiceEngines";
import { Modal } from "../Modal";
import { formatDateTime } from "../analytics/format";
import { Callout } from "./parts";
import { SettingField } from "./SettingField";
import { errorText, isForbidden } from "./errors";
import { ENGINE_LABEL, ENGINE_ORDER } from "./labels";
import {
  changedFields,
  copyKey,
  fieldSpecs,
  fromForm,
  GROUP_ORDER,
  profileKeyError,
  sameSettings,
  toForm,
  usageText,
  type FormValues,
} from "./profileForm";

interface Props {
  data: ProfilesResponse;
  canWrite: boolean;
  environment: string;
  onForbidden: (message: string) => void;
  onChanged: () => Promise<void>;
}

interface CreateState {
  from: Profile | null; // null: a new profile from the engine's defaults
  key: string;
  engine: EngineName;
  note: string;
}

function firstProfile(profiles: Profile[]): Profile | undefined {
  return profiles.find((p) => !p.builtin && p.status === "active") ?? profiles[0];
}

export function ProfilesTab({ data, canWrite, environment, onForbidden, onChanged }: Props) {
  const { profiles, engines } = data;
  const [selectedKey, setSelectedKey] = useState<string | undefined>(() => firstProfile(profiles)?.key);
  const [showArchived, setShowArchived] = useState(false);
  const sel = profiles.find((p) => p.key === selectedKey) ?? firstProfile(profiles);
  const engine = engines.find((e) => e.engine === sel?.engine);
  const specs = useMemo(() => (engine ? fieldSpecs(engine.engine, engine.settingsSchema) : []), [engine]);
  const defaults = engine?.defaults ?? {};

  // The form and the version it was loaded from.
  const [form, setForm] = useState<FormValues>({});
  const [formBase, setFormBase] = useState<{ key: string; version: number } | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [create, setCreate] = useState<CreateState | null>(null);
  const [versions, setVersions] = useState<ProfileVersions | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);

  // The profile selected now. A save, status change or history answers only
  // for the profile still selected: a late answer for A must not show, or
  // load into the form, under B (whose Save would then store A's settings).
  const selKey = useRef<string | undefined>(undefined);
  selKey.current = sel?.key;
  const stillOn = (key: string) => selKey.current === key;

  const load = (p: Profile, settings: Record<string, unknown> = p.settings, version = p.version) => {
    const info = engines.find((e) => e.engine === p.engine);
    const s = info ? fieldSpecs(info.engine, info.settingsSchema) : [];
    setForm(toForm(s, settings, info?.defaults ?? {}));
    setFormBase({ key: p.key, version });
    setNote("");
    setError(null);
  };

  // A different profile picked (or the first load): fill the form from it.
  useEffect(() => {
    if (sel && sel.key !== formBase?.key) {
      load(sel);
      setVersions(null);
      setVersionsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the selection changes
  }, [sel?.key]);

  const { settings, errors } = useMemo(() => fromForm(specs, form), [specs, form]);
  const dirty = !!sel && (Object.keys(errors).length > 0 || !sameSettings(settings, sel.settings));
  const stale = !!sel && !!formBase && formBase.key === sel.key && formBase.version !== sel.version;
  const editable = canWrite && !!sel && !sel.builtin && sel.status === "active";
  // A duplicate of the selected profile takes the form's edits when they are valid.
  const copiesEdits = (from: Profile) => from.key === sel?.key && Object.keys(errors).length === 0;

  const pick = (p: Profile) => {
    setSelectedKey(p.key);
    setToast(null);
  };

  const handle = (e: unknown) => {
    if (isForbidden(e)) onForbidden(errorText(e));
    setError(errorText(e));
  };

  const save = async () => {
    if (!sel) return;
    const key = sel.key;
    setBusy(true);
    setError(null);
    try {
      const saved = await saveProfile(key, sel.version, settings, note.trim() || null);
      await onChanged();
      if (!stillOn(key)) return;
      load(saved);
      setVersions(null);
      setVersionsOpen(false);
      setToast(
        `${saved.key} v${saved.version} saved. New calls use it within 30 seconds; calls in progress keep v${saved.version - 1}.`,
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        await onChanged();
        if (stillOn(key))
          setError(`${errorText(e)}. Someone saved since you opened it: saving again replaces their version with this form; Discard loads theirs.`);
      } else if (isForbidden(e) || stillOn(key)) {
        handle(e);
      }
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: "active" | "archived") => {
    if (!sel) return;
    const key = sel.key;
    setBusy(true);
    setError(null);
    try {
      const p = await setProfileStatus(key, sel.version, status);
      await onChanged();
      if (stillOn(key))
        setToast(status === "archived" ? `${p.key} archived. Arms and test users can no longer pick it.` : `${p.key} is active again.`);
    } catch (e) {
      if (isForbidden(e) || stillOn(key)) handle(e);
    } finally {
      setBusy(false);
    }
  };

  const openCreate = (state: CreateState) => {
    setError(null);
    setCreate(state);
  };

  const submitCreate = async () => {
    if (!create) return;
    setBusy(true);
    setError(null);
    try {
      // A duplicate copies the form as it stands (unsaved edits included).
      const from = create.from;
      const copied = from && copiesEdits(from) ? settings : from?.settings;
      const p = await createProfile({
        key: create.key.trim(),
        engine: create.engine,
        settings: copied ?? {},
        note: create.note.trim() || (from ? `Copy of ${from.key} v${from.version}` : null),
      });
      await onChanged();
      setCreate(null);
      setSelectedKey(p.key);
      setToast(
        from
          ? `Created ${p.key} from ${from.key}. Point a test user at it to try it.`
          : `Created ${p.key} with ${ENGINE_LABEL[p.engine]}'s defaults.`,
      );
    } catch (e) {
      handle(e);
    } finally {
      setBusy(false);
    }
  };

  const shownVersions = versions && versions.key === sel?.key ? versions : null;

  const toggleVersions = (open: boolean) => {
    setVersionsOpen(open);
    if (open && sel && !shownVersions) {
      const key = sel.key;
      getProfileVersions(key)
        .then((v) => {
          if (stillOn(key)) setVersions(v);
        })
        .catch((e) => {
          if (stillOn(key)) setError(errorText(e));
        });
    }
  };

  const createError = create ? profileKeyError(create.key, profiles) : null;
  const archivedCount = profiles.filter((p) => p.status === "archived").length;
  const changed = sel ? changedFields(sel.settings, settings) : [];

  return (
    <div className="ve-columns">
      <section className="panel ve-plist" aria-labelledby="ve-plist-h">
        <div className="ve-row">
          <h2 id="ve-plist-h" className="panel-head" style={{ margin: 0 }}>
            Profiles
          </h2>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canWrite || !sel}
            onClick={() => sel && openCreate({ from: sel, key: copyKey(sel.key, profiles), engine: sel.engine, note: "" })}
          >
            Duplicate
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canWrite}
            onClick={() => openCreate({ from: null, key: "", engine: "gemini_live", note: "" })}
          >
            New
          </button>
        </div>
        {ENGINE_ORDER.map((e) => {
          const info = engines.find((x) => x.engine === e);
          const items = profiles.filter((p) => p.engine === e && (showArchived || p.status === "active"));
          if (!info) return null;
          return (
            <div key={e}>
              <div className="ve-pgroup">
                {ENGINE_LABEL[e]}
                {!info.allowed && <span className="badge ve-warn">not allowed here</span>}
              </div>
              {items.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`ve-pitem${p.key === sel?.key ? " active" : ""}${p.status === "archived" ? " archived" : ""}`}
                  aria-pressed={p.key === sel?.key}
                  onClick={() => pick(p)}
                >
                  <span className="ve-pitem-top">
                    <span className="mono ve-pitem-key">{p.key}</span>
                    {p.status === "archived" && <span className="badge ve-error">archived</span>}
                    <span className="badge">{p.builtin ? "built-in" : `v${p.version}`}</span>
                  </span>
                  <span className="muted ve-small">{usageText(p.usage)}</span>
                </button>
              ))}
            </div>
          );
        })}
        {archivedCount > 0 && (
          <label className="ve-check">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            <span>Show {archivedCount} archived</span>
          </label>
        )}
      </section>

      <section className="panel ve-pform" aria-labelledby="ve-pform-h">
        {toast && (
          <Callout tone="ok" badge="Saved" role="status">
            {toast}
          </Callout>
        )}
        {!sel ? (
          <p className="muted">No profile selected.</p>
        ) : (
          <>
            <div className="ve-ptitle">
              <h2 id="ve-pform-h" className="mono">
                {sel.key}
              </h2>
              <span className="badge">{ENGINE_LABEL[sel.engine]}</span>
              <span className="badge">{sel.builtin ? "built-in" : `v${sel.version}`}</span>
              {sel.status === "archived" && <span className="badge ve-error">archived</span>}
              {dirty && editable && <span className="badge ve-warn">unsaved</span>}
            </div>
            <div className="muted ve-small" style={{ marginBottom: 12 }}>
              {sel.builtin
                ? "Today's env values, never stored"
                : `Saved by ${sel.updatedBy ?? "unknown"} · ${formatDateTime(sel.updatedAt)}`}{" "}
              · {sel.usage.arms.length + sel.usage.overrides > 0 ? `used by ${usageText(sel.usage)}` : "not used"}
              {sel.note && !sel.builtin && ` · “${sel.note}”`}
            </div>

            {sel.builtin && (
              <Callout tone="info" badge="Built-in">
                The engine's default, read from today's env, so it cannot be edited here. Duplicate it to change a
                setting.
              </Callout>
            )}
            {engine && !engine.allowed && (
              <Callout tone="warn" badge="Not allowed">
                <span className="mono">{sel.engine}</span> is not in{" "}
                <span className="mono">VOICE_S2S_ALLOWED_ENGINES</span> on {environment}: no call runs on this profile
                here until a deploy allows it.
              </Callout>
            )}
            {!sel.builtin && (sel.usage.arms.length > 0 || sel.usage.overrides > 0) && editable && (
              <Callout tone="warn" badge="In use">
                {usageText(sel.usage)} run{sel.usage.arms.length + sel.usage.overrides === 1 ? "s" : ""} this profile.
                Saving creates v{sel.version + 1}: new calls pick it up within 30 seconds, calls in progress keep v
                {sel.version}.
              </Callout>
            )}
            {stale && (
              <Callout tone="warn" badge="Newer version">
                v{sel.version} was saved after you opened v{formBase?.version}.
              </Callout>
            )}

            {specs.length === 0 ? (
              <Callout tone="info" badge="No settings">
                This engine has no settings.
              </Callout>
            ) : (
              <fieldset disabled={!editable} style={{ border: "none", padding: 0, margin: 0 }}>
                {GROUP_ORDER.map((group) => {
                  const inGroup = specs.filter((s) => s.group === group);
                  if (!inGroup.length) return null;
                  return (
                    <fieldset key={group}>
                      <legend>{group}</legend>
                      <div className="ve-pfields">
                        {inGroup.map((s) => (
                          <SettingField
                            key={s.name}
                            spec={s}
                            value={form[s.name] ?? null}
                            error={errors[s.name]}
                            defaults={defaults}
                            onChange={(v) => {
                              setForm({ ...form, [s.name]: v });
                              setToast(null);
                            }}
                          />
                        ))}
                      </div>
                    </fieldset>
                  );
                })}
              </fieldset>
            )}

            {error && <p className="error-text">{error}</p>}

            {!sel.builtin && specs.length > 0 && (
              <div className="ve-foot">
                <div>
                  <label htmlFor="ve-pnote">Note for this version</label>
                  <input
                    id="ve-pnote"
                    type="text"
                    maxLength={500}
                    value={note}
                    disabled={!editable}
                    placeholder={changed.length ? `e.g. Change ${changed.join(", ")}` : "e.g. Start the brain early"}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <button type="button" className="btn" disabled={!dirty || busy} onClick={() => load(sel)}>
                  Discard
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!editable || !dirty || busy || Object.keys(errors).length > 0}
                  onClick={save}
                >
                  {busy ? "Saving…" : `Save as v${sel.version + 1}`}
                </button>
              </div>
            )}
            {!canWrite && !sel.builtin && (
              <div className="ve-help">
                Saving on {environment} needs your uid in <span className="mono">VOICE_ENGINE_ADMIN_UIDS</span>.
              </div>
            )}

            {!sel.builtin && (
              <div className="ve-row" style={{ marginTop: 12 }}>
                {sel.status === "active" ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={!canWrite || busy}
                    title="Hide it from arms and test users. Refused while anything uses it."
                    onClick={() => {
                      if (confirm(`Archive ${sel.key}? Arms and test users can no longer pick it.`)) setStatus("archived");
                    }}
                  >
                    Archive
                  </button>
                ) : (
                  <button type="button" className="btn btn-sm" disabled={!canWrite || busy} onClick={() => setStatus("active")}>
                    Make active again
                  </button>
                )}
              </div>
            )}

            {!sel.builtin && (
              <details
                className="ve-details ve-versions"
                open={versionsOpen}
                onToggle={(e) => toggleVersions((e.target as HTMLDetailsElement).open)}
              >
                <summary>Version history</summary>
                {!shownVersions ? (
                  <p className="muted ve-small">Loading…</p>
                ) : (
                  <table className="guide-table">
                    <tbody>
                      {shownVersions.versions.map((v) => (
                        <tr key={v.version}>
                          <td className="mono">v{v.version}</td>
                          <td>
                            {v.createdBy ?? "—"}
                            <div className="muted ve-small">{formatDateTime(v.createdAt)}</div>
                          </td>
                          <td className="ve-small">{v.note || "—"}</td>
                          <td>
                            {v.version !== sel.version && editable && (
                              <button
                                type="button"
                                className="btn btn-sm"
                                title="Fill the form with this version's settings; saving makes them the next version"
                                onClick={() => {
                                  load(sel, v.settings, sel.version);
                                  setNote(`Back to v${v.version}`);
                                }}
                              >
                                Load into form
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </details>
            )}
          </>
        )}
      </section>

      <Modal
        open={create !== null}
        onClose={() => setCreate(null)}
        title={create?.from ? `Duplicate ${create.from.key}` : "New profile"}
        footer={
          <>
            {error && create && <span className="error-text">{error}</span>}
            <div className="spacer" />
            <button type="button" className="btn" onClick={() => setCreate(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || createError !== null}
              onClick={submitCreate}
            >
              {busy ? "Creating…" : "Create v1"}
            </button>
          </>
        }
      >
        {create && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!createError) submitCreate();
            }}
          >
            <label htmlFor="ve-new-key">Key</label>
            <input
              id="ve-new-key"
              type="text"
              className="mono"
              autoFocus
              value={create.key}
              onChange={(e) => setCreate({ ...create, key: e.target.value.trim() })}
            />
            {create.key && createError && <div className="ve-field-error">{createError}</div>}
            <div className="ve-help">
              Arms, test users and call records name it, so pick something that says what it is, e.g.{" "}
              <span className="mono">el-v4t-eager</span>.
            </div>
            <label htmlFor="ve-new-engine">Engine</label>
            <select
              id="ve-new-engine"
              value={create.engine}
              disabled={create.from !== null}
              onChange={(e) => setCreate({ ...create, engine: e.target.value as EngineName })}
            >
              {ENGINE_ORDER.map((e) => (
                <option key={e} value={e}>
                  {ENGINE_LABEL[e]}
                </option>
              ))}
            </select>
            <label htmlFor="ve-new-note">Note</label>
            <input
              id="ve-new-note"
              type="text"
              maxLength={500}
              value={create.note}
              placeholder={create.from ? `Copy of ${create.from.key} v${create.from.version}` : "What it is for"}
              onChange={(e) => setCreate({ ...create, note: e.target.value })}
            />
            <p className="muted ve-small">
              {create.from
                ? `Starts from ${create.from.key}'s ${
                    !dirty || create.from.key !== sel?.key
                      ? "settings"
                      : copiesEdits(create.from)
                        ? "settings, with your unsaved edits"
                        : "saved settings: your unsaved edits have errors, so they are left out"
                  }.`
                : "Starts from the engine's defaults (today's env values)."}
            </p>
          </form>
        )}
      </Modal>
    </div>
  );
}
